import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { HomeDashboardReferralCard } from '../features/home-dashboard/ui/home-dashboard-referral-card';
import { HomeDashboardSharePanel } from '../features/home-dashboard/ui/home-dashboard-share-panel';
import { HomeDashboardSummaryCards } from '../features/home-dashboard/ui/home-dashboard-summary-cards';
import { HomeDashboardTopSection } from '../features/home-dashboard/ui/home-dashboard-top-section';
import { TipsShareSheetOverlay } from '../features/tips-share/ui/tips-share-sheet-overlay';
import { BottomNavPageLayout, HomeDashboardBottomNav } from '../shared/ui';

const FRAME_TOP_PADDING_PX = 20;
const FRAME_SIDE_PADDING_PX = 29;
const FOOTER_TOP_PADDING_PX = 10;
const FOOTER_BOTTOM_PADDING_PX = 13;
const HOME_STACK_GAP_PX = 14;

const Screen = styled.div.attrs({
  'data-ui': 'home-page-screen',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_TOP_PADDING_PX)}, env(safe-area-inset-top))`};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

const ScrollableContent = styled.div.attrs({
  'data-ui': 'home-page-scroll-content',
})<{ $isScrollLocked: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: ${({ $isScrollLocked }) => ($isScrollLocked ? 'hidden' : 'auto')};
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  display: grid;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(HOME_STACK_GAP_PX)};
`;

const BottomNavSlot = styled.div.attrs({
  'data-ui': 'home-page-bottom-nav-slot',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  padding-top: ${({ theme }) => theme.scale.px(FOOTER_TOP_PADDING_PX)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(
      FOOTER_BOTTOM_PADDING_PX,
    )}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

export function HomePage() {
  const navigate = useNavigate();
  const [isQrFullscreenOpen, setQrFullscreenOpen] = useState(false);

  const openQrFullscreen = () => {
    if (isQrFullscreenOpen) {
      return;
    }

    setQrFullscreenOpen(true);
  };

  const closeActiveSheet = () => {
    setQrFullscreenOpen(false);
  };

  return (
    <BottomNavPageLayout
      frameDataUi="home-page-frame"
      overlay={
        <TipsShareSheetOverlay
          isOpen={isQrFullscreenOpen}
          onClose={closeActiveSheet}
        />
      }
      pageDataUi="home-page"
      stickyFooter={
        <BottomNavSlot>
          <HomeDashboardBottomNav
            activeTab="home"
            onOpenHome={() => {
              navigate('/home');
            }}
            onOpenMyPage={() => {
              navigate('/my-page');
            }}
          />
        </BottomNavSlot>
      }
    >
      <Screen>
        <ScrollableContent $isScrollLocked={isQrFullscreenOpen}>
          <HomeDashboardTopSection
            onOpenProfile={() => {
              navigate('/settings');
            }}
            onOpenReferral={() => {
              navigate('/referral');
            }}
            onOpenWithdrawal={() => {
              navigate('/withdrawal');
            }}
          />
          <HomeDashboardSummaryCards
            onOpenStatement={() => {
              navigate('/statement');
            }}
          />
          <HomeDashboardReferralCard
            onOpenReferral={() => {
              navigate('/referral');
            }}
          />
          <HomeDashboardSharePanel onOpenQrFullscreen={openQrFullscreen} />
        </ScrollableContent>
      </Screen>
    </BottomNavPageLayout>
  );
}
